import { useQuery } from '@tanstack/react-query';
import { pnidApi } from '../api';
import { queryKeys } from '../queries/keys';
import type { ClickTarget, ClickTargetColumn } from '../types';
import { bboxArea } from '../utils/geometry';

const NO_CLICK_TARGETS: ClickTarget[] = [];

// Module-level, so React Query reruns the sort only when the column changes.
function largestFirst(column: ClickTargetColumn | null): ClickTarget[] {
  if (column === null) return NO_CLICK_TARGETS;
  return [...column.targets].sort(
    (first, second) => bboxArea(second.bbox) - bboxArea(first.bbox),
  );
}

/**
 * One drawing's click targets, largest box first, read once per drawing.
 *
 * Largest first is the canvas's draw order, so a smaller target paints over a
 * larger one that overlaps it. The targets change only when a generator reruns,
 * which no SME action does, so the read never goes stale in a session. The same
 * array comes back on every render until the column changes, so it is safe as
 * a memo dependency.
 *
 * While `enabled` is false nothing is fetched and the list is empty, even after
 * an earlier read. The list is empty until the read lands, and stays empty if
 * the read fails or the drawing was never generated: the SME can still drag
 * every region.
 */
export function useClickTargets(
  docId: string | null | undefined,
  enabled: boolean,
): ClickTarget[] {
  const isReadEnabled = enabled && Boolean(docId);
  const query = useQuery({
    queryKey: queryKeys.clickTargets(docId ?? ''),
    queryFn: async () => {
      try {
        return await pnidApi.getClickTargets(docId!);
      } catch (error) {
        console.error(`Click-target read failed for document ${docId}`, error);
        throw error;
      }
    },
    enabled: isReadEnabled,
    staleTime: Infinity,
    select: largestFirst,
  });
  // A disabled query still returns the data it cached before it was disabled.
  return isReadEnabled ? (query.data ?? NO_CLICK_TARGETS) : NO_CLICK_TARGETS;
}
